import * as Yup from 'yup';
import { validateIdFile } from './generalInfoSchema';
import {
  licenseFieldConfigDefaults,
  getHcpSignupWizardAttachmentFileValidationOpts,
} from './hcpSignupDefaults';
import {
  normalizeWizardDateInput,
  todayIsoDateLocal,
  isSignupEndIsoDateAllowed,
} from './hcpSignupDateUtils';

function licenseNumberPattern(mode, min, max) {
  if (String(mode).toLowerCase() === 'numeric') {
    return new RegExp(`^[0-9]{${min},${max}}$`);
  }
  return new RegExp(`^[A-Za-z0-9\\-]{${min},${max}}$`);
}

/** @param {object} licenseFieldConfig merged */
export function buildLicenseItemSchema(licenseFieldConfig = {}) {
  const c = { ...licenseFieldConfigDefaults, ...licenseFieldConfig };
  const prefixMust = c.prefixRequired === true || c.prefixOptional === false;
  const maxPref = c.prefixMaxLength ?? 10;
  const minPref = c.prefixMinLength ?? 1;
  const numRe = licenseNumberPattern(
    c.licenseNumberPattern,
    c.licenseNumberMinLength,
    c.licenseNumberMaxLength
  );
  const authMin = c.authorityNameMinLength ?? 2;
  const authMax = c.authorityNameMaxLength ?? 120;
  const fileOpts = getHcpSignupWizardAttachmentFileValidationOpts();

  const prefixSchema = prefixMust
    ? Yup.string()
        .trim()
        .required('Prefix is required')
        .min(minPref, `Prefix must be at least ${minPref} characters`)
        .max(maxPref, `Prefix must be at most ${maxPref} characters`)
    : Yup.string()
        .trim()
        .nullable()
        .test(
          'prefix-len',
          `Prefix must be ${minPref}–${maxPref} characters when provided`,
          (v) => {
            if (!v || !String(v).trim()) return true;
            const s = String(v).trim();
            return s.length >= minPref && s.length <= maxPref;
          }
        );

  return Yup.object({
    _rowKey: Yup.string().nullable(),
    prefix: prefixSchema,
    licenseNumber: Yup.string()
      .trim()
      .required('License number is required')
      .matches(
        numRe,
        String(c.licenseNumberPattern).toLowerCase() === 'numeric'
          ? `Use ${c.licenseNumberMinLength}–${c.licenseNumberMaxLength} digits`
          : `Use ${c.licenseNumberMinLength}–${c.licenseNumberMaxLength} characters (letters, digits, hyphens)`
      ),
    issuingCountry: Yup.string().required('Issuing country is required'),
    authorityName: Yup.string()
      .trim()
      .required('Authority name is required')
      .min(authMin, `Authority name must be ${authMin}–${authMax} characters`)
      .max(authMax, `Authority name must be ${authMin}–${authMax} characters`),
    issueDate: Yup.string()
      .transform((v) => (v == null ? '' : normalizeWizardDateInput(String(v))))
      .required('Issue date is required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
      .test('not-future', 'Issue date cannot be after today', (v) => {
        if (!v) return false;
        return v <= todayIsoDateLocal();
      }),
    expiryDate: Yup.string()
      .transform((v) => (v == null ? '' : normalizeWizardDateInput(String(v))))
      .required('Expiry date is required')
      .matches(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
      .test(
        'not-past',
        'Expiry date must be today or in the future',
        isSignupEndIsoDateAllowed
      )
      .test(
        'after-issue',
        'Expiry must be after issue date',
        function afterIssue(v) {
          const issue = this.parent.issueDate;
          if (!v || !issue) return true;
          const a = normalizeWizardDateInput(String(issue));
          const b = normalizeWizardDateInput(String(v));
          if (!/^\d{4}-\d{2}-\d{2}$/.test(a) || !/^\d{4}-\d{2}-\d{2}$/.test(b))
            return true;
          return b > a;
        }
      ),
    attachmentFile: Yup.mixed().nullable(),
    existingAttachmentUploadId: Yup.number().nullable(),
    attachmentLabel: Yup.string().nullable(),
    licenseRowId: Yup.number().nullable(),
  }).test('attachment', 'Attachment is required', function attRule(row) {
    // Full path is required: bare "attachmentFile" becomes a *root* Formik error
    // (Yup inner path has no index), so FileFieldInput never sees errors.licenses[i].attachmentFile.
    const filePath = this.path
      ? `${this.path}.attachmentFile`
      : 'attachmentFile';
    if (!c.licenseAttachmentRequired) return true;
    if (row?.existingAttachmentUploadId) return true;
    if (row?.attachmentFile instanceof File) {
      const err = validateIdFile(row.attachmentFile, fileOpts);
      if (err) return this.createError({ path: filePath, message: err });
      return true;
    }
    return this.createError({
      path: filePath,
      message: `Attachment is required (${String(
        fileOpts.acceptExt.join(', ')
      ).toUpperCase()} — max ${fileOpts.maxMb}MB)`,
    });
  });
}

export function buildLicensesFormSchema(licenseFieldConfig) {
  const item = buildLicenseItemSchema(licenseFieldConfig);
  return Yup.object({
    licenses: Yup.array().of(item).min(1, 'Add at least one license'),
  });
}
